'use client';

import { AnimatePresence, motion } from 'framer-motion';
import Image from 'next/image';
import React, { useCallback, useEffect, useState } from 'react';
import { useDisplay } from '@/app/(users)/[locationId]/(dashboard)/(store)/displays/hooks/display-hooks';
import { useOrdersDisplay } from '@/app/(users)/display/[displayId]/hooks/use-orders-display';
import { Order } from '@/interface/order';
import { getOrderAlphaNumber } from '@/lib/order-util';
import { cn, DisplayInitialColors } from '@/lib/utils';

const CAROUSEL_INTERVAL_MS = 5000;

function useBannerUrls(display: { banner?: string | null; banners?: string[] | null } | null | undefined): string[] {
  if (!display) return [];
  if (display.banners && display.banners.length > 0) return display.banners;
  if (display.banner) return [display.banner];
  return [];
}

export function DisplayView({ displayId }: { displayId: string }) {
  const { data: display } = useDisplay(displayId, { listenDisplayEvents: true });
  const { orders } = useOrdersDisplay(display?.location_id ?? '', display?.id ?? '');
  const bannerUrls = useBannerUrls(display);

  const [currentIndex, setCurrentIndex] = useState(0);

  const goTo = useCallback(
    (index: number) => {
      if (bannerUrls.length === 0) return;
      setCurrentIndex(((index % bannerUrls.length) + bannerUrls.length) % bannerUrls.length);
    },
    [bannerUrls.length]
  );

  const goNext = useCallback(() => goTo(currentIndex + 1), [currentIndex, goTo]);

  useEffect(() => {
    if (bannerUrls.length <= 1) return;
    const id = setInterval(goNext, CAROUSEL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [bannerUrls.length, goNext]);

  if (!display) return null;

  const hasBanners = bannerUrls.length > 0;

  return (
    <div
      className="bg-background h-dvh w-full"
      style={Object.entries(display?.colors ?? DisplayInitialColors).reduce(
        (acc, [key, value]) => {
          acc[`--${key}`] = `hsl(${value})`;
          return acc;
        },
        {} as Record<string, string>
      )}
    >
      <div className="bg-background mx-auto grid aspect-9/16 h-full md:aspect-video w-full md:grid-cols-3 gap-2 rounded-md p-2 select-none">
        <div
          className={cn(
            'bg-border grid-cols-1 col-span-1 md:col-span-2 grid w-full md:grid-cols-2 gap-4 rounded-md border p-2 text-xs',
            {
              'col-span-3': !hasBanners,
            }
          )}
        >
          <div className="flex w-full flex-col items-center gap-2">
            <div className="from-accent to-accent/50 text-accent-foreground flex w-full flex-row items-center justify-center gap-2 rounded-md border bg-linear-to-t p-4 shadow inset-shadow-2xs inset-shadow-white/25">
              <h4 className="text-4xl font-bold">En Preparación</h4>
            </div>
            <div className="grid w-full grid-cols-4 gap-2 sm:grid-cols-2 md:grid-cols-4">
              <AnimatePresence mode="popLayout">
                {orders
                  .filter(order => order.status === 'pending' && order.number != null)
                  .sort((a, b) => b.updated_at.localeCompare(a.updated_at))
                  .map(order => (
                    <OrderCard key={order.id} order={order} />
                  ))}
              </AnimatePresence>
            </div>
          </div>
          <div className="flex w-full flex-col items-center gap-2">
            <div className="text-accent-foreground flex w-full flex-row items-center justify-center gap-2 rounded-md border bg-linear-to-t from-green-400 to-green-300/50 p-4 shadow-md inset-shadow-2xs inset-shadow-white/25">
              <h4 className="text-4xl font-bold">Para Retirar</h4>
            </div>
            <div className="grid w-full grid-cols-4 gap-2 sm:grid-cols-2 md:grid-cols-4">
              <AnimatePresence mode="popLayout">
                {orders
                  .filter(order => order.status === 'ready' && order.number != null)
                  .map(order => (
                    <OrderCard key={order.id} order={order} />
                  ))}
              </AnimatePresence>
            </div>
          </div>
        </div>

        {hasBanners && (
          <div className="bg-background relative h-full w-full overflow-hidden rounded-md shadow-md">
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={currentIndex}
                animate={{ opacity: 1 }}
                className="absolute inset-0"
                exit={{ opacity: 0 }}
                initial={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
              >
                <Image
                  alt={`Banner ${currentIndex + 1}`}
                  className="h-full w-full object-cover"
                  height={1000}
                  src={bannerUrls[currentIndex]!}
                  width={1000}
                />
              </motion.div>
            </AnimatePresence>
            {bannerUrls.length > 1 && (
              <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5">
                {bannerUrls.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    aria-label={`Ir a imagen ${i + 1}`}
                    className={cn(
                      'h-1.5 rounded-full transition-all',
                      i === currentIndex ? 'bg-primary w-6' : 'bg-primary/40 w-1.5 hover:bg-primary/60'
                    )}
                    onClick={() => goTo(i)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function OrderCard({ order }: { order: Order }) {
  const [isReady, setIsReady] = useState(false);

  // Detectar cuando el estado cambia a 'ready'
  useEffect(() => {
    if (order.status === 'ready') {
      setIsReady(true);
      // Resetear después de la animación de shake
      const timer = setTimeout(() => {
        setIsReady(false);
      }, 1300); // Un poco más que la duración del shake

      return () => clearTimeout(timer);
    }
  }, [order.status]);

  const shakeAnimation = {
    opacity: 1,
    x: [0, -20, 20, -20, 20, -15, 15, -10, 10, -5, 5, 0],
    y: [0, -10, 10, -10, 10, -8, 8, -5, 5, -3, 3, 0],
    rotate: [0, -5, 5, -5, 5, -3, 3, -2, 2, -1, 1, 0],
    scale: [1, 1.1, 0.95, 1.15, 0.9, 1.2, 0.85, 1.1, 0.95, 1.05, 1],
    transition: {
      duration: 1.2,
      ease: 'easeOut',
      times: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
    },
  };

  const readyIdleAnimation = {
    opacity: 1,
    x: 0,
    y: 0,
    rotate: 0,
    scale: 1,
    transition: {
      duration: 0.5,
      ease: 'easeOut',
    },
  };

  const normalAnimation = {
    opacity: 1,
    x: 0,
    y: 0,
    rotate: 0,
    scale: 1,
    transition: {
      duration: 0.3,
    },
  };

  const getAnimationState = () => {
    if (isReady) return shakeAnimation;
    if (order.status === 'ready') return readyIdleAnimation;

    return normalAnimation;
  };

  return (
    <motion.div
      layout
      animate={getAnimationState()}
      className={cn(
        'relative flex min-h-14 flex-shrink-0 items-center justify-center overflow-hidden rounded-md border-2 bg-white p-2 sm:min-h-20 sm:p-3 md:min-h-24 md:p-4',
        {
          'border-0': order.status === 'ready',
          'border-gray-300': order.status !== 'ready',
        }
      )}
      exit={{ opacity: 0, y: -100 }}
      initial={{ opacity: 0, y: 100 }}
      layoutId={`order-${order.id}`}
    >
      <p
        className={cn('relative z-10 text-center text-lg font-bold sm:text-xl md:text-2xl', {
          'text-green-400': order.status === 'ready',
          'text-gray-800': order.status !== 'ready',
        })}
      >
        {getOrderAlphaNumber(order.number ?? 0)}
      </p>
    </motion.div>
  );
}
